import React, { useState } from 'react';

import { cn } from '../../lib/utils';
import { api } from '../../lib/api';
import { useAuthStore, getUserTier } from '../../stores/auth.store';

interface MessageBubbleProps {
  role: 'user' | 'assistant';
  content: string;
  /** Agent's light background colour class (e.g. 'bg-ray-light') */
  agentBgClass: string;
  /** Agent name for screen readers */
  agentName: string;
  /** Session ID for saving insights */
  sessionId?: string | null;
}

/**
 * Individual chat message bubble.
 * User messages appear right-aligned with neutral background.
 * Agent messages appear left-aligned with the agent's light colour background.
 * Early Testers see a "Save Insight" button on agent messages.
 */
export function MessageBubble({
  role,
  content,
  agentBgClass,
  agentName,
  sessionId,
}: MessageBubbleProps): React.JSX.Element {
  const isUser = role === 'user';
  const authState = useAuthStore();
  const tier = getUserTier(authState);
  const showSaveInsight = !isUser && tier === 'early_tester';

  const [saved, setSaved] = useState<false | 'insight' | 'action'>(false);
  const [saving, setSaving] = useState(false);

  async function handleSave(asAction: boolean) {
    if (saving || saved) return;
    setSaving(true);
    try {
      // Only send sessionId if it looks like a real UUID (not a guest-xxx placeholder)
      const validSessionId = sessionId && !sessionId.startsWith('guest-') ? sessionId : undefined;
      await api.post('/insights', {
        sourceText: content,
        sessionId: validSessionId,
        tags: asAction ? ['action'] : [],
      });
      setSaved(asAction ? 'action' : 'insight');
    } catch {
      // silently fail
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      className={cn('flex w-full', isUser ? 'justify-end' : 'justify-start')}
      aria-label={isUser ? 'Your message' : `${agentName}'s message`}
    >
      <div className={cn('max-w-[80%]', !isUser && 'group relative')}>
        <div
          className={cn(
            'rounded-lg px-4 py-3 text-sm leading-relaxed',
            isUser
              ? 'bg-surface-muted text-text-primary'
              : cn(agentBgClass, 'text-text-primary'),
          )}
        >
          {content.split('\n').map((line, i) => (
            <React.Fragment key={i}>
              {i > 0 && <br />}
              {line.split(/(\*\*[^*]+\*\*)/).map((part, j) =>
                part.startsWith('**') && part.endsWith('**') ? (
                  <strong key={j}>{part.slice(2, -2)}</strong>
                ) : (
                  <span key={j}>{part}</span>
                )
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Save Insight / Action buttons — visible on hover for Early Testers */}
        {showSaveInsight && (
          <div className={cn(
            'absolute -bottom-5 left-2 flex gap-1 transition',
            'opacity-0 group-hover:opacity-100 focus-within:opacity-100',
          )}>
            {saved ? (
              <span className="rounded-full bg-gail-light px-2 py-0.5 text-[10px] font-medium text-gail">
                {saved === 'action' ? '✓ Action saved' : '✓ Insight saved'}
              </span>
            ) : saving ? (
              <span className="rounded-full bg-surface-muted px-2 py-0.5 text-[10px] font-medium text-text-muted">
                Saving…
              </span>
            ) : (
              <>
                <button
                  onClick={() => handleSave(false)}
                  className="rounded-full bg-surface-muted px-2 py-0.5 text-[10px] font-medium text-text-muted hover:bg-border hover:text-text-secondary"
                  aria-label="Save as insight"
                  title="Save as Insight"
                >
                  💡 Insight
                </button>
                <button
                  onClick={() => handleSave(true)}
                  className="rounded-full bg-surface-muted px-2 py-0.5 text-[10px] font-medium text-text-muted hover:bg-border hover:text-text-secondary"
                  aria-label="Save as action"
                  title="Save as Action Item"
                >
                  ✅ Action
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
